import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChevronRight, Shield, Heart, Users, Trophy, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const timeline = [
  {
    year: '1946',
    title: 'Gründung des Vereins',
    desc: 'Aus dem Zusammenschluss von Teutonia Hedem und der Spielvereinigung Vehlage entstand die SpVgg. Hedem-Vehlage e.V. – der Grundstein des heutigen HSC Alswede.',
  },
  {
    year: '1975',
    title: 'Turnhalle an der Fiesteler Straße',
    desc: 'Mit dem Bau und Einzug in die Turnhalle an der Fiesteler Str. in Alswede konnten wir unseren Breitensportangebot erweitern',
  },
  {
    year: '1996',
    title: 'Neues Sportgelände am Schanzeweg',
    desc: 'Mit dem Grundsanierung Bau des heutigen Sportgeländes im Schanzeweg in Alswede entstand DER zentrale Treffpunkt für uns.'
  },
  {
    year: '2000',
    title: 'Umbenennung zum HSC Alswede',
    desc: 'Mit der verwaltungstechnischen Eingliederung von Alswede in die Stadt Lübbecke erfolgte die Umbenennung zum heutigen HSC Alswede von 1946 e.V.',
  },
  {
    year: '2003',
    title: 'Einweihung des Sportlerheims',
    desc: 'Das vereinseigene Sportlerheim am Schanzeweg 7 wurde eingeweiht – ein Heimathafen für alle Vereinsmitglieder und Freunde des HSC.',
  },
  {
    year: '2005',
    title: 'Das Jahrhundertspiel',
    desc: 'Ein historisches Ereignis: Der HSC Alswede trat gegen den SV Werder Bremen an – ein unvergessliches Fußballfest, das zur Vereinslegende wurde.',
  },
  {
    year: '2025',
    title: 'Neuauflage & Expansion',
    desc: 'Mini-Kicker, neue Darts-Abteilung, Ü32-Altliga, starke Jugendarbeit und die Neuauflage des Jahrhundertspiels: Der HSC wächst mit großen Schritten.',
  },
  {
    year: 'Heute',
    title: '450+ Mitglieder',
    desc: 'Der HSC Alswede ist ein lebendiger Verein mit über 450 Mitgliedern, 10+ Sportarten, 20+ Sportgruppen und einem starken Fokus auf Familie, Jugend und Gemeinschaft.',
  },
];

const values = [
  {
    icon: Heart,
    title: 'Gemeinschaft',
    desc: 'Wir sind mehr als ein Verein – wir sind eine Familie. Jedes Mitglied zählt, jede Stimme wird gehört.',
  },
  {
    icon: Users,
    title: 'Inklusion',
    desc: 'Sport für alle – unabhängig von Alter, Können oder Hintergrund. Beim HSC ist jeder willkommen.',
  },
  {
    icon: Trophy,
    title: 'Ehrenamt',
    desc: 'Unser Verein lebt vom Engagement seiner Mitglieder. Ehrenamtliches Engagement ist bei uns Tradition.',
  },
  {
    icon: Star,
    title: 'Jugendarbeit',
    desc: 'Kinder sind unsere Zukunft. Wir investieren bewusst in tolle Angebote für die Jüngsten.',
  },
];

const board = [
  {
    name: 'Patrick Brüls',
    role: '1. Vorsitzender',
    email: 'patrick.bruels@hsc-alswede.de',
    phone: '+49 156 79244181',
    image: '/images/vorstand/patrick-bruels.jpg',
  },
  {
    name: 'Dennis Burkamp',
    role: '2. Vorsitzender',
    email: 'dennis.burkamp@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/dennis-burkamp.jpg',
  },
  {
    name: 'Ulrich Haseloh',
    role: '3. Vorsitzender & Altliga-Obmann',
    email: 'ulrich.haseloh@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/ulrich-haseloh.jpg',
  },
  {
    name: 'Mara Voss',
    role: 'Kassiererin',
    email: 'mara.voss@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/mara-voss.jpg',
  },
  {
    name: 'Juliane Hellmich',
    role: 'stellvertr. Kassiererin',
    email: 'juliane.hellmich@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/juliane-hellmich.jpg',
  },
  {
    name: 'Sharon Nadine Brown',
    role: '1. Geschäftsführerin & Fußballobfrau Frauen',
    email: 'sharon.nadine.brown@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/sharon-brown.jpg',
  },
  {
    name: 'Marcel Reinies',
    role: '2. Geschäftsführer',
    email: 'Marcel.Reinies@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/marcel-reinies.jpg',
  },
];

// Erweiterter Vorstand
const extendedBoard = [

  {
    name: 'Patrick Nehls',
    role: 'Sponsoringbeauftragter',
    email: 'patrick.nehls@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/patrick-nehls.jpg',
  },
  {
    name: 'Claudia Brüls',
    role: 'Jugendkoordinatorin Breitensport',
    email: 'claudia.bruels@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/claudia-bruels.jpg',
  },
  {
    name: 'Daniela Vortmeier',
    role: 'Sozialwartin',
    email: 'daniela.vortmeier@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/daniela-vortmeier.jpg',
  },
  {
    name: 'Patrick Braun',
    role: 'Breitsportbeauftragter',
    email: 'breitensport@hsc-alswede.de',
    phone: '',
    image: '/images/vorstand/patrick-braun.jpg',
  }

];
const MemberCard = ({ member }) => (
  <Card className="group rounded-3xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.05)] hover:shadow-[0_18px_50px_rgba(17,17,17,0.12)] transition-all hover:-translate-y-1">
    
    <div className="flex flex-col items-center text-center">
      
      {member.image ? (
        <img
          src={member.image}
          alt={member.name}
          className="w-28 h-28 rounded-full object-cover border-4 border-[#F5C200]/20 shadow-md mb-4"
        />
      ) : (
        <div className="w-28 h-28 bg-[#F5C200]/15 rounded-full flex items-center justify-center mb-4">
          <span className="font-bold text-[#1a1a1a] text-2xl">
            {member.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
          </span>
        </div>
      )}

      <div className="font-semibold text-[#1a1a1a] text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {member.name}
      </div>

      <div className="text-sm text-gray-500 mt-1 min-h-[40px]">
        {member.role}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 w-full space-y-1">
        
        <a href={`mailto:${member.email}`} className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors truncate block">
          {member.email}
        </a>

        {member.phone && (
          <a href={`tel:${member.phone.replace(/\s+/g, '')}`} className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors block">
            {member.phone}
          </a>
        )}
      </div>

    </div>
  </Card>
);
export default function UeberUns() {
  useEffect(() => {
    document.title = 'Über uns | HSC Alswede von 1946 – Geschichte & Vereinswerte';
  }, []);

  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-[#1a1a1a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, #F5C200 0%, transparent 60%)'
        }} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-[#F5C200] text-sm mb-4">
              <Link to="/" className="text-gray-400 hover:text-gray-200 transition-colors">Start</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Über uns</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Über den HSC Alswede
            </h1>
            <p className="text-gray-400 max-w-xl">
              Seit 1946 vereinen wir Menschen durch Sport, Gemeinschaft und
              die Leidenschaft für Bewegung im Herzen von Alswede.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
                Unsere Werte
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Was uns ausmacht
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <Card className="rounded-2xl border border-black/[0.08] bg-white p-6 shadow-[0_10px_30px_rgba(17,17,17,0.05)] h-full text-center">
                  <div className="w-12 h-12 bg-[#F5C200]/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <v.icon className="w-6 h-6 text-[#1a1a1a]" />
                  </div>
                  <h3 className="font-bold text-[#1a1a1a] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{v.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14 lg:py-20 bg-[#FAFAF9]" data-testid="history-timeline">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
                Geschichte
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Unsere Vereinsgeschichte
              </h2>
              <p className="mt-3 text-gray-600">Von der Gründung bis heute – ein Verein, der Alswede prägt.</p>
            </div>
          </FadeIn>
          <div className="relative pl-8 before:absolute before:left-3.5 before:top-0 before:h-full before:w-px before:bg-gray-200">
            {timeline.map((item, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="relative mb-8 last:mb-0">
                  {/* Node */}
                  <div className="absolute -left-8 top-1 w-7 h-7 bg-[#F5C200] rounded-full flex items-center justify-center shadow-[0_0_0_4px_white]">
                    <div className="w-2.5 h-2.5 bg-[#1a1a1a] rounded-full" />
                  </div>
                  <Card className="rounded-2xl border border-black/[0.08] bg-white p-5 shadow-[0_10px_30px_rgba(17,17,17,0.04)]">
                    <div className="text-sm font-bold text-[#F5C200] mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.year}
                    </div>
                    <h3 className="font-semibold text-[#1a1a1a] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </Card>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
                Vorstand
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Unser Vorstand und Funktionäre
              </h2>
              <p className="mt-3 text-gray-600 max-w-xl mx-auto">
                Ehrenamtlich und mit vollem Herzblut – unser Vorstand steht für den HSC Alswede.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {board.map((member, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <MemberCard member={member} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
      {/* Erweiterter Vorstand */}
      <section className="py-14 lg:py-20 bg-[#FAFAF9]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
                Erweiterter Vorstand
                <span className="w-8 h-0.5 bg-[#F5C200] rounded-full"></span>
              </div>
              <h2 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Weitere Verantwortliche
              </h2>
              <p className="mt-3 text-gray-600 max-w-xl mx-auto">
                Sie kümmern sich um die wichtigen Bereiche unseres Vereins.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {extendedBoard.map((member, i) => (
                <FadeIn key={i} delay={i * 0.08}>
                  <MemberCard member={member} />
                </FadeIn>
))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-12 bg-[#F5C200]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Gehör dazu!
            </h2>
            <p className="text-[#1a1a1a]/70 mb-6">
              Überzeug dich selbst – komm zu einem Probetraining oder melde dich als Mitglied an.
            </p>
            <Link to="/kontakt">
              <Button className="h-12 px-8 bg-[#1a1a1a] text-white hover:bg-[#333] font-semibold rounded-xl border-0 shadow-none transition-colors">
                Jetzt Mitglied werden
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}